import { isAfter, isBefore, sub } from "date-fns";
import type { Dispatch, SetStateAction } from "react";

import Expandable from "@/components/Expandable";
import { Card } from "@/components/ui/card";
import DatePicker from "@/components/ui/date-picker";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Typography } from "@/components/ui/typography";
import { useConfigOptions } from "@/hooks/useConfigOptions";
import { cn } from "@/lib/utils";

type FiltersProps = {
  fromDate: Date | undefined;
  setFromDate: Dispatch<SetStateAction<Date | undefined>>;
  toDate: Date | undefined;
  setToDate: Dispatch<SetStateAction<Date | undefined>>;
  selectedFlag: string;
  setSelectedFlag: Dispatch<SetStateAction<string>>;
  selectedBranch: string;
  setSelectedBranch: Dispatch<SetStateAction<string>>;
  selectedPlant: string;
  setSelectedPlant: Dispatch<SetStateAction<string>>;
  selectedUser: string;
  setSelectedUser: Dispatch<SetStateAction<string>>;
  selectedRepairSystem: string;
  setSelectedRepairSystem: Dispatch<SetStateAction<string>>;
  selectedKitSize: string;
  setSelectedKitSize: Dispatch<SetStateAction<string>>;
};

export default function Filters({
  fromDate,
  setFromDate,
  toDate,
  setToDate,
  selectedFlag,
  setSelectedFlag,
  selectedBranch,
  setSelectedBranch,
  selectedPlant,
  setSelectedPlant,
  selectedUser,
  setSelectedUser,
  selectedRepairSystem,
  setSelectedRepairSystem,
  selectedKitSize,
  setSelectedKitSize,
}: FiltersProps) {
  const { jobFlagsOptions, branchOptions, plantOptions, usersOptions, repairSystemOptions, allKits } =
    useConfigOptions();
  return (
    <Card className=" gap-4 p-4">
      <Expandable
        titleClassName="items-start"
        title={
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex w-[230px] items-center justify-center gap-4 md:w-[260px] md:justify-start">
              <Typography>From</Typography>
              <DatePicker
                date={fromDate}
                btnClassname={cn(!fromDate && "text-red-500")}
                disabled={(date) => isAfter(date, new Date()) || isBefore(date, sub(new Date(), { years: 1 }))}
                onChange={(date) => {
                  setFromDate(date);
                  if (toDate && isAfter(date, toDate)) {
                    setToDate(undefined);
                  }
                }}
              />
            </div>
            <div className="flex w-[230px] items-center justify-center gap-4 md:w-[260px] md:justify-start">
              <Typography>To</Typography>
              <DatePicker
                btnClassname={cn(!toDate && "text-red-500")}
                disabled={(date) => (!!fromDate && isBefore(date, fromDate)) || isAfter(date, new Date())}
                date={toDate}
                onChange={(date) => setToDate(date)}
              />
            </div>
          </div>
        }
      >
        <div className="flex flex-wrap items-center gap-4">
          <Select value={selectedFlag} onValueChange={setSelectedFlag}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="select flag" />
            </SelectTrigger>
            <SelectContent>
              {jobFlagsOptions.map((option, index) => {
                return (
                  <SelectItem
                    key={`flag-${option.value}-${index}`}
                    className="cursor-pointer"
                    value={String(option.value)}
                  >
                    {option.title}
                  </SelectItem>
                );
              })}
              <SelectItem key="flag-all" className="cursor-pointer" value="''">
                All
              </SelectItem>
            </SelectContent>
          </Select>
          <Select value={selectedBranch} onValueChange={setSelectedBranch}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="select branch" />
            </SelectTrigger>
            <SelectContent>
              {branchOptions.map((option, index) => {
                return (
                  <SelectItem
                    key={`branch-${option.value}-${index}`}
                    className="cursor-pointer"
                    value={String(option.value)}
                  >
                    {option.title}
                  </SelectItem>
                );
              })}
              <SelectItem key="branch-all" className="cursor-pointer" value="''">
                All
              </SelectItem>
            </SelectContent>
          </Select>
          <Select value={selectedPlant} onValueChange={setSelectedPlant}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="select plant" />
            </SelectTrigger>
            <SelectContent>
              {plantOptions.map((option, index) => {
                return (
                  <SelectItem
                    key={`plant-${option.value}-${index}`}
                    className="cursor-pointer"
                    value={String(option.value)}
                  >
                    {option.title}
                  </SelectItem>
                );
              })}
              <SelectItem key="plant-all" className="cursor-pointer" value="''">
                All
              </SelectItem>
            </SelectContent>
          </Select>
          <Select value={selectedUser} onValueChange={setSelectedUser}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="select user" />
            </SelectTrigger>
            <SelectContent>
              {usersOptions.map((option, index) => {
                return (
                  <SelectItem
                    key={`user-${option.value}-${index}`}
                    className="cursor-pointer"
                    value={String(option.value)}
                  >
                    {option.title}
                  </SelectItem>
                );
              })}
              <SelectItem key="user-all" className="cursor-pointer" value="''">
                All
              </SelectItem>
            </SelectContent>
          </Select>
          <Select value={selectedRepairSystem} onValueChange={setSelectedRepairSystem}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="select repair system" />
            </SelectTrigger>
            <SelectContent>
              {repairSystemOptions.map((option, index) => {
                return (
                  <SelectItem
                    key={`repair-${option.value}-${index}`}
                    className="cursor-pointer"
                    value={String(option.value)}
                  >
                    {option.title}
                  </SelectItem>
                );
              })}
              <SelectItem key="repair-all" className="cursor-pointer" value="''">
                All
              </SelectItem>
            </SelectContent>
          </Select>
          <Select value={selectedKitSize} onValueChange={setSelectedKitSize}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="select kit size" />
            </SelectTrigger>
            <SelectContent>
              {allKits.map((option, index) => {
                return (
                  <SelectItem
                    key={`kit-${option.value}-${index}`}
                    className="cursor-pointer"
                    value={String(option.value)}
                  >
                    {option.title}
                  </SelectItem>
                );
              })}
              <SelectItem key="kit-all" className="cursor-pointer" value="''">
                All
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Expandable>
    </Card>
  );
}
